import { Building2, CalendarPlus, Check, ClipboardList, SlidersHorizontal, UserPlus } from 'lucide-react'
import { Button, ButtonGlyph, buttonClass, Link, type ButtonIcon } from '../../../core/index.js'
import type { SetupStep, SetupStepKey } from './todayData.js'

const ICON: Record<SetupStepKey, ButtonIcon> = { record: Building2, units: Building2, plan: SlidersHorizontal, workers: UserPlus, publish: CalendarPlus, census: ClipboardList }

interface SetupListProps {
  steps: readonly SetupStep[]
  configHref: string
  monthHref: string
  onAddWorker: () => void
  onRecordCensus: () => void
}

/**
 * The empty facility's set-up list (facility operations dashboard plan §3a): in Needs you's place while there is
 * nothing to run, the steps in the order they unlock each other, each with what it unlocks and the button that does it;
 * a step whose prerequisite is not done waits, and the list goes away once every step is done.
 */
export function SetupList({ steps, configHref, monthHref, onAddWorker, onRecordCensus }: SetupListProps) {
  const done = new Set(steps.filter((step) => step.done).map((step) => step.key))
  const hrefOf: Partial<Record<SetupStepKey, string>> = { record: `${configHref}?tab=details`, units: `${configHref}?tab=details`, plan: `${configHref}?tab=scheduling&sub=census`, publish: `${monthHref}&detail=publish` }
  return (
    <ol className="flex flex-col divide-y divide-marine/10">
      {steps.map((step, index) => {
        const waiting = step.needs.some((need) => !done.has(need))
        const Icon = ICON[step.key]
        const href = hrefOf[step.key]
        return (
          <li key={step.key} className="grid grid-cols-[2rem_1fr_auto] items-start gap-3 py-3">
            <span className={`flex h-7 w-7 items-center justify-center rounded-full border-2 text-xs font-black ${step.done ? 'border-green bg-green text-white' : waiting ? 'border-silver text-silver' : 'border-marine text-marine'}`} aria-label={step.done ? 'Done' : `Step ${index + 1}`}>
              {step.done ? <Check className="h-3.5 w-3.5" /> : index + 1}
            </span>
            <div className="min-w-0">
              <div className="text-[13px] font-black text-ink">{step.title}</div>
              {!step.done && <p className="mt-0.5 text-xs leading-relaxed text-ink-mid">{step.body}</p>}
              {!step.done && <p className="mt-1 text-[11px] text-ink-mid">Unlocks: <span className="font-bold text-ink">{step.unlocks}</span></p>}
            </div>
            {step.done ? (
              href ? (
                <Link to={href} className="pt-1 text-xs font-bold text-marine hover:text-marine-dk">
                  Change
                </Link>
              ) : null
            ) : step.key === 'workers' ? (
              <Button icon={Icon} size="sm" onClick={onAddWorker}>
                Add a worker
              </Button>
            ) : step.key === 'census' ? (
              <Button icon={Icon} size="sm" disabled={waiting} onClick={onRecordCensus}>
                Record
              </Button>
            ) : href && !waiting ? (
              <Link to={href} className={buttonClass({ size: 'sm' })}>
                <ButtonGlyph icon={Icon} size="sm" />
                {step.key === 'plan' ? 'Set the plan' : step.key === 'publish' ? 'Publish' : 'Add units'}
              </Link>
            ) : (
              <span className="pt-1 text-[11px] text-ink-mid">Needs {step.needs.filter((need) => !done.has(need)).join(' and ')}</span>
            )}
          </li>
        )
      })}
    </ol>
  )
}
